import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { entityConfig, removeEntity, setAllEntities, upsertEntity, withEntities } from '@ngrx/signals/entities';
import { Bank } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { ChildBankAccountInformation } from '../entities/child-onboarding/bank-account-information';
import {
  ChildOnboardingSituation,
  UnderageOnboardingLegalGuardianshipStatus,
} from '../entities/child-onboarding/child-onboarding-situation.model';
import { ChildOnboarding } from '../entities/child-onboarding/child-onboarding.model';
import { PersonalInformationChild } from '../entities/child-onboarding/personal-information';
import { UploadedFile } from '../entities/child-onboarding/uploaded-file';
import { Country } from '../entities/country.entity';
import { NationalityTaxAndEmploymentInformation } from '../entities/nationality-tax-and-employment-information';
import { ResidenceInformation } from '../entities/residence-information';

const BASE_ROUTE = `${environment.apihost}/api/child-onboarding`;

interface ChildOnboardingState {
  error: string | null;

  selectedId: string | null;
  childOnboardingsLoading: boolean;
  childOnboardingsLoaded: boolean;

  deletingChildOnboarding: boolean;
  deletedChildOnboardingSuccessfully: boolean;

  situations: ChildOnboardingSituation[];
  selectedSituation: ChildOnboardingSituation | null;
  situationsLoading: boolean;
  situationsLoaded: boolean;

  //legal guardian 1
  sendingPersonalInformation: boolean;
  sentPersonalInformationSuccessfully: boolean;
  sendingResidenceInformation: boolean;
  sentResidenceInformationSuccessfully: boolean;
  sendingPhoneNumber: boolean;
  sentPhoneNumberSuccessfully: boolean;
  sendingNationalityTaxInformation: boolean;
  sentNationalityTaxInformationSuccessfully: boolean;
  sendingBankAccountInformation: boolean;
  sentBankAccountInformationSuccessfully: boolean;
  sendingIdentificationInformation: boolean;
  sentIdentificationInformationSuccessfully: boolean;
  sendingLegalGuardian2Information: boolean;
  sentLegalGuardian2InformationSuccessfully: boolean;
  sendingSignUp: boolean;
  sentSignUpSuccessfully: boolean;
  resendingConfirmationMail: boolean;
  resentConfirmationMailSuccessfully: boolean;
  sendingContractConfirmation: boolean;
  sentContractConfirmationSuccessfully: boolean;

  //legal guardian 2
  childOnboardingToAccept: ChildOnboarding | null;
  sendingLegalGuardian2Check: boolean;
  sentLegalGuardian2CheckSuccessfully: boolean;
  sendingLegalGuardian2Accept: boolean;
  sentLegalGuardian2AcceptSuccessfully: boolean;

  countries: Country[];
  bankData: Bank;
}

const childOnboardingConfig = entityConfig({
  entity: type<ChildOnboarding>(),
  collection: 'childOnboarding',
  selectId: (childOnboarding) => childOnboarding.onboardingId,
});

const initialState: ChildOnboardingState = {
  error: null,
  selectedId: null,
  childOnboardingsLoading: false,
  childOnboardingsLoaded: false,
  deletingChildOnboarding: false,
  deletedChildOnboardingSuccessfully: false,
  situations: [],
  selectedSituation: null,
  situationsLoading: false,
  situationsLoaded: false,
  sendingPersonalInformation: false,
  sentPersonalInformationSuccessfully: false,
  sendingResidenceInformation: false,
  sentResidenceInformationSuccessfully: false,
  sendingPhoneNumber: false,
  sentPhoneNumberSuccessfully: false,
  sendingNationalityTaxInformation: false,
  sentNationalityTaxInformationSuccessfully: false,
  sendingBankAccountInformation: false,
  sentBankAccountInformationSuccessfully: false,
  sendingIdentificationInformation: false,
  sentIdentificationInformationSuccessfully: false,
  sendingLegalGuardian2Information: false,
  sentLegalGuardian2InformationSuccessfully: false,
  sendingSignUp: false,
  sentSignUpSuccessfully: false,
  resendingConfirmationMail: false,
  resentConfirmationMailSuccessfully: false,
  sendingContractConfirmation: false,
  sentContractConfirmationSuccessfully: false,
  childOnboardingToAccept: null,
  sendingLegalGuardian2Check: false,
  sentLegalGuardian2CheckSuccessfully: false,
  sendingLegalGuardian2Accept: false,
  sentLegalGuardian2AcceptSuccessfully: false,
  countries: [],
  bankData: null,
};

export const ChildOnboardingStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities(childOnboardingConfig),

  withComputed(({ selectedId, childOnboardingEntityMap }) => ({
    selectedChildOnboarding: computed(() => {
      const id = selectedId();
      return childOnboardingEntityMap()[id] ?? null;
    }),
  })),
  withProps(
    ({
      error,
      selectedChildOnboarding,
      situations,
      selectedSituation,
      sendingPersonalInformation,
      sentPersonalInformationSuccessfully,
      sendingResidenceInformation,
      sentResidenceInformationSuccessfully,
      sendingPhoneNumber,
      sentPhoneNumberSuccessfully,
      sendingNationalityTaxInformation,
      sentNationalityTaxInformationSuccessfully,
      sendingBankAccountInformation,
      sentBankAccountInformationSuccessfully,
      sendingIdentificationInformation,
      sentIdentificationInformationSuccessfully,
      sendingLegalGuardian2Information,
      sentLegalGuardian2InformationSuccessfully,
      sendingSignUp,
      sentSignUpSuccessfully,
      sendingContractConfirmation,
      sentContractConfirmationSuccessfully,
      childOnboardingToAccept,
      sendingLegalGuardian2Check,
      sentLegalGuardian2CheckSuccessfully,
      sendingLegalGuardian2Accept,
      sentLegalGuardian2AcceptSuccessfully,
      countries,
      bankData,
    }) => ({
      error$: toObservable(error),
      selectedChildOnboarding$: toObservable(selectedChildOnboarding),
      situations$: toObservable(situations),
      selectedSituation$: toObservable(selectedSituation),
      sendingPersonalInformation$: toObservable(sendingPersonalInformation),
      sentPersonalInformationSuccessfully$: toObservable(sentPersonalInformationSuccessfully),
      sendingResidenceInformation$: toObservable(sendingResidenceInformation),
      sentResidenceInformationSuccessfully$: toObservable(sentResidenceInformationSuccessfully),
      sendingPhoneNumber$: toObservable(sendingPhoneNumber),
      sentPhoneNumberSuccessfully$: toObservable(sentPhoneNumberSuccessfully),
      sendingNationalityTaxInformation$: toObservable(sendingNationalityTaxInformation),
      sentNationalityTaxInformationSuccessfully$: toObservable(sentNationalityTaxInformationSuccessfully),
      sendingBankAccountInformation$: toObservable(sendingBankAccountInformation),
      sentBankAccountInformationSuccessfully$: toObservable(sentBankAccountInformationSuccessfully),
      sendingIdentificationInformation$: toObservable(sendingIdentificationInformation),
      sentIdentificationInformationSuccessfully$: toObservable(sentIdentificationInformationSuccessfully),
      sendingLegalGuardian2Information$: toObservable(sendingLegalGuardian2Information),
      sentLegalGuardian2InformationSuccessfully$: toObservable(sentLegalGuardian2InformationSuccessfully),
      sendingSignUp$: toObservable(sendingSignUp),
      sentSignUpSuccessfully$: toObservable(sentSignUpSuccessfully),
      sendingContractConfirmation$: toObservable(sendingContractConfirmation),
      sentContractConfirmationSuccessfully$: toObservable(sentContractConfirmationSuccessfully),
      childOnboardingToAccept$: toObservable(childOnboardingToAccept),
      sendingLegalGuardian2Check$: toObservable(sendingLegalGuardian2Check),
      sentLegalGuardian2CheckSuccessfully$: toObservable(sentLegalGuardian2CheckSuccessfully),
      sendingLegalGuardian2Accept$: toObservable(sendingLegalGuardian2Accept),
      sentLegalGuardian2AcceptSuccessfully$: toObservable(sentLegalGuardian2AcceptSuccessfully),
      countries$: toObservable(countries),
      bankData$: toObservable(bankData),
    }),
  ),
  withMethods((store, httpClient = inject(HttpClient)) => ({
    clear() {
      patchState(store, { ...initialState });
    },
    async loadChildOnboardingSituations(force?: boolean) {
      patchState(store, { situationsLoading: true, error: null });
      try {
        if (!force && store.situations().length > 0) {
          return;
        }
        const situations = await firstValueFrom(httpClient.get<ChildOnboardingSituation[]>(`${BASE_ROUTE}/situations`));
        patchState(store, { situations });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { situationsLoading: false, situationsLoaded: true });
      }
    },
    async loadChildOnboardings(force?: boolean) {
      patchState(store, { childOnboardingsLoading: true, error: null });
      try {
        if (!force && store.childOnboardingIds().length > 0) {
          return;
        }

        const childOnboardings = await firstValueFrom(httpClient.get<ChildOnboarding[]>(`${BASE_ROUTE}/list`));
        patchState(store, setAllEntities(childOnboardings, childOnboardingConfig), { childOnboardingsLoaded: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { childOnboardingsLoading: false });
      }
    },
    async deleteChildOnboardingById(id: string) {
      patchState(store, { deletingChildOnboarding: true, deletedChildOnboardingSuccessfully: false, error: null });
      try {
        await firstValueFrom(httpClient.delete<void>(`${BASE_ROUTE}/${id}`));
        patchState(store, removeEntity(id, childOnboardingConfig), { deletedChildOnboardingSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { deletingChildOnboarding: false });
      }
    },

    async setSelected(selectedId: string) {
      patchState(store, { selectedId });
    },
    async resetSelected() {
      patchState(store, { selectedId: null });
    },

    //startChildOnboarding
    async setSelectedSituation(situationType: UnderageOnboardingLegalGuardianshipStatus) {
      const selectedSituation = store.situations()?.find((s) => s.type === situationType) ?? null;
      patchState(store, { selectedSituation, error: null });
    },

    async sendPersonalInformation(personalInformation: PersonalInformationChild) {
      patchState(store, { sendingPersonalInformation: true, sentPersonalInformationSuccessfully: false, error: null });
      try {
        const { gender, name, lastName, birthDay, country, city } = personalInformation;
        const changes: Partial<ChildOnboarding> = {
          anrede: gender ?? null,
          vorname: name ?? null,
          nachname: lastName ?? null,
          geburtsdatum: birthDay ?? null,
          geburtsland: country ?? null,
          geburtsort: city ?? null,
        };

        let childOnboarding: ChildOnboarding = null;
        const onboardingId = store.selectedId();
        if (onboardingId) {
          changes.onboardingId = onboardingId;

          childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        } else {
          changes.childOnboardingSituation = store.selectedSituation()?.type ?? null;

          childOnboarding = await firstValueFrom(httpClient.post<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
          patchState(store, { selectedId: childOnboarding.onboardingId });
        }

        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentPersonalInformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingPersonalInformation: false });
      }
    },
    async sendResidenceInformation(residenceInformation: ResidenceInformation) {
      patchState(store, { sendingResidenceInformation: true, sentResidenceInformationSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const { street, streetNumber, zip, city, addition, country } = residenceInformation;
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          strasse: street ?? null,
          hausnummer: streetNumber ?? null,
          plz: zip ?? null,
          ort: city ?? null,
          adresszusatz: addition ?? null,
          land: country ?? null,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentResidenceInformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingResidenceInformation: false });
      }
    },
    async sendPhoneNumber(mobileNumber: string) {
      patchState(store, { sendingPhoneNumber: true, sentPhoneNumberSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const changes: Partial<ChildOnboarding> = { onboardingId, mobileNumber };
        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentPhoneNumberSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingPhoneNumber: false });
      }
    },
    async sendNationalityTaxInformation(data: NationalityTaxAndEmploymentInformation) {
      patchState(store, { sendingNationalityTaxInformation: true, sentNationalityTaxInformationSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const { taxCountry, tin, nationality } = data;
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          steuerland: taxCountry ?? null,
          steuerid: tin ?? null,
          nationality: nationality ?? null,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentNationalityTaxInformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingNationalityTaxInformation: false });
      }
    },
    async sendBankAccountInformation(data: ChildBankAccountInformation) {
      patchState(store, { sendingBankAccountInformation: true, sentBankAccountInformationSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const { iban, bankAccountOwner } = data;
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          iban: iban ?? null,
          kontoinhaber: bankAccountOwner ?? null,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentBankAccountInformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingBankAccountInformation: false });
      }
    },
    async sendIdentificationInformation(files: UploadedFile[], hasSentAlready: boolean) {
      patchState(store, { sendingIdentificationInformation: true, sentIdentificationInformationSuccessfully: false, error: null });
      if (hasSentAlready) {
        patchState(store, { sentIdentificationInformationSuccessfully: true, sendingIdentificationInformation: false });
        return;
      }
      try {
        const onboardingId = store.selectedId();
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          uploadFileReferences: files,
          childOnboardingSituation: store.selectedSituation()?.type ?? null,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentIdentificationInformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingIdentificationInformation: false });
      }
    },
    async sendLegalGuardian2Information(customerId: string, birthDay: string) {
      patchState(store, { sendingLegalGuardian2Information: true, sentLegalGuardian2InformationSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          legalGuardian2Id: customerId ?? null,
          legalGuardian2Birthday: birthDay ?? null,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentLegalGuardian2InformationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingLegalGuardian2Information: false });
      }
    },
    async signUp(email: string, password: string) {
      patchState(store, { sendingSignUp: true, sentSignUpSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
          accountCreationData: {
            email: email ?? null,
            password: password ?? null,
          },
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentSignUpSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingSignUp: false });
      }
    },
    async resendConfirmationMail() {
      patchState(store, { resendingConfirmationMail: true, resentConfirmationMailSuccessfully: false, error: null });
      try {
        const childOnboarding = store.selectedChildOnboarding();
        await firstValueFrom(
          httpClient.post(`${BASE_ROUTE}/resendConfirmationMail`, {
            email: childOnboarding.cEmail,
            onboardingId: childOnboarding.onboardingId,
          }),
        );
        patchState(store, { resentConfirmationMailSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { resendingConfirmationMail: false });
      }
    },
    async submitContract() {
      patchState(store, { sendingContractConfirmation: true, sentContractConfirmationSuccessfully: false, error: null });
      try {
        const onboardingId = store.selectedId();
        const changes: Partial<ChildOnboarding> = {
          onboardingId,
        };

        const childOnboarding = await firstValueFrom(httpClient.patch<ChildOnboarding>(`${BASE_ROUTE}/data?finishEntry=true`, changes));
        patchState(store, upsertEntity(childOnboarding, childOnboardingConfig), { sentContractConfirmationSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingContractConfirmation: false });
      }
    },

    //legal guardian 2
    async sendLegalGuardian2Check(onboardingId: string, childBirthDate: string) {
      patchState(store, { sendingLegalGuardian2Check: true, sentLegalGuardian2CheckSuccessfully: false, error: null });
      try {
        const childOnboardingToAccept = store.childOnboardingEntityMap()[onboardingId];
        const date = childOnboardingToAccept?.geburtsdatum;
        const storedDate = date ? new Date(date).toISOString().split('T')[0] : null;
        if (storedDate === childBirthDate) {
          patchState(store, {
            childOnboardingToAccept,
            sentLegalGuardian2CheckSuccessfully: true,
          });
        } else {
          patchState(store, {
            sentLegalGuardian2CheckSuccessfully: false,
            error: 'child-onboarding.legal-guardian-2-confirmation.invalid-birth-date',
          });
        }
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingLegalGuardian2Check: false });
      }
    },
    async sendLegalGuardian2Accept() {
      patchState(store, { sendingLegalGuardian2Accept: true, sentLegalGuardian2AcceptSuccessfully: false, error: null });
      try {
        const onboardingId = store.childOnboardingToAccept()?.onboardingId;
        const date = store.childOnboardingToAccept()?.geburtsdatum;
        const childBirthday = date ? new Date(date).toISOString().split('T')[0] : null;
        await firstValueFrom(
          httpClient.post<ChildOnboarding>(`${BASE_ROUTE}/confirm`, {
            onboardingId: onboardingId ?? null,
            childBirthday,
          }),
        );
        patchState(store, { sentLegalGuardian2AcceptSuccessfully: true });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      } finally {
        patchState(store, { sendingLegalGuardian2Accept: false });
      }
    },

    async resetSuccessStates() {
      patchState(store, {
        sentPersonalInformationSuccessfully: false,
        sentResidenceInformationSuccessfully: false,
        sentPhoneNumberSuccessfully: false,
        sentNationalityTaxInformationSuccessfully: false,
        sentBankAccountInformationSuccessfully: false,
        sentIdentificationInformationSuccessfully: false,
        sentLegalGuardian2InformationSuccessfully: false,
        sentSignUpSuccessfully: false,
        sentContractConfirmationSuccessfully: false,
        resentConfirmationMailSuccessfully: false,
        sentLegalGuardian2CheckSuccessfully: false,
        sentLegalGuardian2AcceptSuccessfully: false,
        deletedChildOnboardingSuccessfully: false,
      });
    },

    async loadBankByIban(iban: string) {
      try {
        patchState(store, { error: null });

        const bankData = await firstValueFrom(httpClient.get<Bank>(`${environment.apihost}/api/bank/${iban}`));
        patchState(store, { bankData });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      }
    },
    async clearBankData() {
      patchState(store, { bankData: null });
    },

    async loadCountries(force?: boolean) {
      if (!force && store.countries().length > 0) {
        return;
      }
      patchState(store, { error: null });
      try {
        const countries = await firstValueFrom(httpClient.get<Country[]>(`${environment.apihost}/api/country/all`));
        patchState(store, { countries });
      } catch (ex) {
        patchState(store, { error: ex.error?.message });
      }
    },
  })),
);
